// src/components/registration/steps/userGroupSteps/UserGroupsStep3Confirm.jsx

import React, { useState } from "react";
import PropTypes from "prop-types";
import MultiStyleButton from "../../../../systemUtils/MultiStyleButton";
import OutputScreen from "../../../../systemUtils/OutputScreen";
import LoadingCircle from "../../../../../utils/LoadingCircle";
import { finalizeUserStructure } from "../../functions/finalizeUserStructure";
import "../UserSystemCard.css";

// Firebase
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";

/**
 * UserGroupsStep3Confirm
 *
 * This step:
 *   1) Confirms the user group hierarchy (shows a summary).
 *   2) Finalizes the structure (calls finalizeUserStructure).
 *   3) Shows an OutputScreen with the results.
 *   4) NEW: Lets the current user select their own group & role,
 *      and writes them to /[company]/shared/Users/<userEmail>.
 */
const UserGroupsStep3Confirm = ({
  userGroups,
  onContinue,
  company,
  collectionBrand,
  userEmail,
  setCardStep,
}) => {
  // Existing UI states
  const [isProcessing, setIsProcessing] = useState(false);
  const [operationResults, setOperationResults] = useState(null);
  const [showOutput, setShowOutput] = useState(false);

  // NEW: Local state for the current user's group & role selection
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedRole, setSelectedRole] = useState("");

  /**
   * handleFinalConfirm
   *  1) Validate user’s selected group/role
   *  2) Call finalizeUserStructure => writes userGroups & sends invites
   *  3) Write the current user doc => /[company]/shared/Users/<email>
   *  4) Show OutputScreen
   */
  const handleFinalConfirm = async () => {
    try {
      // 1) Validate group & role selection
      if (!selectedGroup || !selectedRole) {
        alert("Please select your own group and role before confirming.");
        return;
      }

      setIsProcessing(true);

      // 2) finalizeUserStructure
      const systemNameConfig = "companyConfig";
      const results = await finalizeUserStructure({
        userGroups,
        company,
        collectionBrand,
        systemNameConfig,
        userEmail, // the user who is issuing the invites
      });
      setOperationResults(results);

      // 3) Write the user doc to /[company]/shared/Users/<email>
      const app = getApp();
      const functions = getFunctions(app);
      const firestoreOps = httpsCallable(functions, "firestoreOperations");

      await firestoreOps({
        operation: "write",
        company,
        // Using TechPerformanceCentre as the collection brand
        collectionBrand: "TechPerformanceCentre",
        systemName: "Users",
        uniqueId: userEmail,
        documentData: {
          dateCreated: new Date().toISOString(),
          email: userEmail,
          userGroup: selectedGroup,
          userRole: selectedRole,
          state: "Active",
        },
      });

      // 4) Show OutputScreen
      setShowOutput(true);
    } catch (error) {
      console.error("Error in handleFinalConfirm:", error);
      alert("An error occurred while finalizing. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * handleOutputContinue
   *  Called when the user dismisses the output screen
   *  => triggers onContinue() to move to next step.
   */
  const handleOutputContinue = () => {
    onContinue();
  };

  /**
   * handleEdit
   *  If user wants to go back & edit user groups,
   *  sets cardStep back to 2.
   */
  const handleEdit = () => {
    setCardStep(2);
  };

  // If we've shown the OutputScreen with results
  if (showOutput && operationResults) {
    return (
      <OutputScreen
        title="User Group Setup & Invitations"
        info="Below are the results of your user group configuration and invitations."
        dataObjects={[
          {
            title: "Group Document",
            data: {
              "Firestore Doc ID": operationResults.structureDocId,
              "Total Invites": operationResults.totalInvites,
            },
          },
          {
            title: "Invite Details",
            // Transform each array item into an object property
            data: operationResults.inviteDetails.reduce((acc, item, idx) => {
              acc[`Invite #${idx + 1}`] = item;
              return acc;
            }, {}),
          },
        ]}
        onContinue={handleOutputContinue}
      />
    );
  }

  return (
    <div className="step3-confirm-container">
      <h2 className="card-title">Confirm User Groups</h2>
      <p className="card-subtitle">
        Review your user group hierarchy, and select your own group & role
        before finalizing.
      </p>

      {/* Loading overlay if finalizing */}
      {isProcessing && (
        <div className="loading-overlay">
          <LoadingCircle label="Finalizing your setup..." />
        </div>
      )}

      {/* Summary of existing user groups */}
      {!isProcessing && (
        <div className="user-groups-summary">
          {userGroups.length > 0 ? (
            <ul className="group-list">
              {userGroups.map((group, gIndex) => (
                <li key={gIndex} className="group-item">
                  <strong>{group.groupName}</strong> (Hierarchy:{" "}
                  {group.hierarchy})
                  {group.roles.length > 0 ? (
                    <ul className="role-list">
                      {group.roles.map((role, rIndex) => (
                        <li key={rIndex} className="role-item">
                          {role.roleName}:{" "}
                          {role.users.length > 0
                            ? role.users.join(", ")
                            : "No users assigned."}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="no-roles-message">No Roles assigned.</p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="no-groups-message">No User Groups configured.</p>
          )}
        </div>
      )}

      {/* NEW: UI to select the current user's group & role */}
      {!isProcessing && userGroups.length > 0 && (
        <div className="select-current-user-role">
          <h3 className="card-title">Select Your Own Group & Role</h3>

          <div>
            <label htmlFor="selectedGroup">Group:</label>
            <select
              id="selectedGroup"
              value={selectedGroup}
              onChange={(e) => {
                setSelectedGroup(e.target.value);
                setSelectedRole(""); // reset if group changes
              }}
            >
              <option value="">--Select Group--</option>
              {userGroups.map((g, idx) => (
                <option key={idx} value={g.groupName}>
                  {g.groupName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="selectedRole">Role:</label>
            <select
              id="selectedRole"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              disabled={!selectedGroup}
            >
              <option value="">--Select Role--</option>
              {selectedGroup &&
                userGroups
                  .find((g) => g.groupName === selectedGroup)
                  ?.roles.map((r, idx) => (
                    <option key={idx} value={r.roleName}>
                      {r.roleName}
                    </option>
                  ))}
            </select>
          </div>
        </div>
      )}

      {/* Final Navigation Buttons */}
      {!isProcessing && (
        <div className="continue-button-container">
          <MultiStyleButton
            label="Edit"
            buttonType="cancel"
            onClick={handleEdit}
          />
          <MultiStyleButton
            label="Confirm"
            buttonType="approve"
            onClick={handleFinalConfirm}
          />
        </div>
      )}
    </div>
  );
};

UserGroupsStep3Confirm.propTypes = {
  userGroups: PropTypes.array.isRequired,
  onContinue: PropTypes.func.isRequired,
  company: PropTypes.string.isRequired,
  collectionBrand: PropTypes.string.isRequired,
  userEmail: PropTypes.string.isRequired,
  setCardStep: PropTypes.func.isRequired,
};

export default UserGroupsStep3Confirm;
